import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Image as ChakraImage,
    IconButton,
    Input,
    Stack,
    Spinner,
    useToast,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';

// Type définissant une photo stockée sur le serveur
interface Photo {
    id: string;
    url: string;
    name?: string;
}

const PhotoGrid: React.FC = () => {
    // Base URL de l'API depuis la variable d'environnement
    const API_URL = process.env.REACT_APP_API_URL || '';
    const [photos, setPhotos] = useState<Photo[]>([]);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    // Fonction pour charger la liste des images depuis le backend
    const fetchImages = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/images`);
            if (!res.ok) {
                const text = await res.text();
                console.error('Erreur server fetchImages:', text);
                toast({ title: 'Erreur serveur', status: 'error' });
                return;
            }
            if (res.status === 204) {
                setPhotos([]);
                return;
            }
            const ids: string[] = await res.json();
            // Transforme chaque id en URL exploitable
            const list = ids.map(id => ({
                id,
                url: `${API_URL}/api/image/${id}`,
            }));
            setPhotos(list);
        } catch (err) {
            console.error(err);
            toast({ title: 'Erreur lors du chargement des images', status: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchImages();
    }, []);

    // Gère l'importation de fichiers vers le backend
    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('image', file);

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/image/upload`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: formData,
            });
            if (!res.ok) {
                const errText = await res.text();
                console.error('Erreur server handleUpload:', errText);
                throw new Error('Upload échoué');
            }
            toast({ title: 'Image uploadée', status: 'success' });
            fetchImages(); // recharge la grille
        } catch (err) {
            console.error(err);
            toast({ title: 'Erreur lors du téléchargement', status: 'error' });
        } finally {
            setLoading(false);
            e.target.value = '';
        }
    };

    // Supprime une image côté serveur
    const handleDelete = async (id: string) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/image/${id}`, {
                method: 'DELETE',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) {
                const errText = await res.text();
                console.error('Erreur server handleDelete:', errText);
                throw new Error('Suppression échouée');
            }
            toast({ title: 'Image supprimée', status: 'info' });
            setPhotos(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            console.error(err);
            toast({ title: 'Erreur lors de la suppression', status: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={4}>
            <Input
                type="file"
                accept="image/*"
                onChange={handleUpload}
                mb={4}
                disabled={loading}
            />

            {loading && <Spinner mb={4} />}

            <Grid templateColumns="repeat(auto-fill, minmax(150px, 1fr))" gap={4}>
                {photos.map(photo => (
                    <Box key={photo.id} position="relative">
                        <ChakraImage
                            src={photo.url}
                            alt={photo.name || photo.id}
                            objectFit="cover"
                            w="100%"
                            h="150px"
                            borderRadius="md"
                        />

                        <Stack direction="row" position="absolute" top={2} right={2} spacing={1}>
                            <IconButton
                                aria-label="Supprimer"
                                icon={<DeleteIcon />}
                                size="sm"
                                onClick={() => handleDelete(photo.id)}
                                isDisabled={loading}
                            />
                        </Stack>
                    </Box>
                ))}
            </Grid>
        </Box>
    );
};

export default PhotoGrid;
